/**
 * ``ClusterLayer`` — non-visual React component that registers the
 * MapLibre cluster + cluster-count layers on the snapshot GeoJSON
 * source from :file:`snapshot-layer-impl.ts`, wires the click-to-
 * expand interaction, and pushes per-recompute summaries to the
 * ``aria-live`` region.
 *
 * Stream A PR 2 / commit M4 — implements the low-zoom cluster
 * aggregation surface per ADR-016 Open Item 5 (z 0–6 cluster,
 * z 7+ individual) and Flow 03's behavioral contract.
 *
 * # Why a separate component
 *
 * The cluster layer is **two** layers (circle + symbol for count)
 * that key off the **same source** as the snapshot layer. Splitting
 * the component lets the snapshot layer own source lifetime while
 * the cluster layer owns its two sibling layers + the click handler
 * + the a11y region updates. Both components depend on the source
 * already being registered — they mount only after the Map
 * captures a loaded map instance.
 *
 * # Why a separate impl file
 *
 * ESLint's ``react-refresh/only-export-components`` rule warns
 * when a ``.tsx`` module exports non-component values alongside
 * components. The helpers (:func:`attachClusterLayers`,
 * :func:`handleClusterClick`, :func:`computeClusterCounts`, +
 * constants) live in :file:`cluster-layer-impl.ts` so Fast Refresh
 * keeps working cleanly on this component during dev — mirrors the
 * :file:`snapshot-layer.tsx` / :file:`snapshot-layer-impl.ts`
 * pattern.
 *
 * # Interaction model — click to expand (orchestrator decision 6)
 *
 * Clicking a cluster glyph queries MapLibre for the cluster's
 * *expansion zoom* (the zoom level at which the cluster's members
 * would resolve into individual markers under the current
 * ``clusterMaxZoom`` setting) and animates the camera there via
 * :meth:`Map.flyTo`. No popup, no hover affordance — Flow 03's
 * hover-tooltip is Phase 4 polish (out-of-scope per M4 brief
 * decision 7).
 *
 * # A11y posture (orchestrator decision 5)
 *
 * MapLibre's clusters are rendered to WebGL — they have no DOM
 * nodes for a screen reader to find. We surface a coarse
 * ``aria-live="polite"`` region inside the map's container that
 * announces a one-sentence summary on each viewport recompute:
 * "12 clusters visible covering 248 producers." Debounced to 500 ms
 * (:const:`CLUSTER_SUMMARY_DEBOUNCE_MS`) so panning doesn't thrash
 * the screen reader. Per-cluster ARIA labels are a future surface
 * (popup hover affordance — Flow 03 Phase 4 polish).
 *
 * # Why the recompute summary lives at moveend, not data
 *
 * MapLibre's ``data`` event fires every time the source's data
 * changes (e.g., SSE deltas via :func:`SnapshotLayer`'s ``setData``
 * call). Announcing on every SSE delta would be noise. ``moveend``
 * fires once after pan/zoom completes — the operator's natural
 * "what's on screen now" moment. ``sourcedata`` is also included so
 * the initial snapshot + SSE deltas eventually surface (debounced).
 *
 * Cross-references:
 *   - REQ-UI-010 (cluster rendering — see :file:`cluster-styling.ts`
 *     module docstring for the REQ-UI-010 phrasing caveat).
 *   - REQ-UI-011 (zoom 7+ individual markers — the
 *     ``clusterMaxZoom: 6`` on the source terminates aggregation).
 *   - REQ-UI-026 (server state via TanStack Query — the snapshot is
 *     consumed indirectly via the source the snapshot layer owns).
 *   - REQ-UI-040 / REQ-UI-046 (theme + status semantics — colors
 *     flow through :func:`getStatusColor` per
 *     :file:`cluster-styling.ts`).
 *   - REQ-UI-059 (keyboard / a11y — the ``aria-live`` region is the
 *     map's a11y bridge for the cluster surface).
 *   - ADR-016 Open Item 5 + §2 Stage 4 + §4.
 *   - docs/engineering/detail/storyboards/flows/03-pan-zoom-cluster/README.md.
 */
import { useEffect, useRef } from "react";
import type { MapMouseEvent, Map as MapLibreMap } from "maplibre-gl";
import { CLUSTER_LAYER_ID, clusterSummaryMessage } from "@/map/cluster-styling";
import { SNAPSHOT_SOURCE_ID } from "@/map/snapshot-layer-impl";
import { stableOverrideKey, type PlatformColorConfig } from "@/map/marker-styling";
import type { SnapshotResponse } from "@/map/snapshot-types";
import {
  CLUSTER_SUMMARY_DEBOUNCE_MS,
  attachClusterLayers,
  computeClusterCounts,
  detachClusterLayers,
  handleClusterClick,
} from "@/map/cluster-layer-impl";

/**
 * Props for :func:`ClusterLayer`.
 *
 * Cross-references:
 *   - REQ-UI-010.
 */
export interface ClusterLayerProps {
  /** Live MapLibre map instance. The caller (:class:`Map`) only mounts
   *  this component after the map's ``load`` event has fired AND after
   *  the snapshot layer has registered the source. */
  map: MapLibreMap;
  /**
   * Current snapshot response, or ``null`` while loading.
   *
   * The cluster layer **must** know about the snapshot — not because
   * it consumes the data (the MapLibre source does that internally
   * via clustering) but because the cluster-layer registration
   * gates on the snapshot source's existence. The source is created
   * by :class:`SnapshotLayer`'s effect on the snapshot transition
   * from ``null`` → non-``null``; without this prop the cluster
   * layer's attach effect would run only once on map-load (when the
   * source doesn't yet exist) and never re-attach.
   *
   * The 2026-05-27 empty-map diagnostic surfaced this race: at zoom
   * levels ≤ :const:`CLUSTER_MAX_ZOOM` (= 6), clustered features are
   * filtered out of the snapshot layer (``filter: ["!", ["has",
   * "point_count"]]`` in :func:`attachSnapshotLayer`) and only the
   * cluster layer can render them. With the layer never attached,
   * zoom 0–6 paints nothing even when the snapshot returns
   * producers. Before PR #243 the snapshot's ``time >= since``
   * filter returned empty so the race never manifested; once that
   * filter was dropped, the race became visible at any low-zoom
   * view that includes producer positions.
   */
  snapshot: SnapshotResponse | null;
  /**
   * DOM element receiving the ``aria-live`` summary text. The
   * provider component owns the element so the region can stay
   * mounted across cluster-layer remounts (e.g., snapshot
   * detach/attach cycles); the layer just writes into it.
   *
   * When ``null``, the summary updates are skipped (the layer still
   * mounts the visual surface) — :class:`Map` always provides a
   * non-null ref in production.
   */
  liveRegionRef?: React.RefObject<HTMLElement | null>;
  /**
   * The active Profile's platform marker colour config (``map.platform``;
   * ADR-033 §9.2). Threaded into the cluster bubble's colour (REQ-UI-086):
   * ``single`` mode paints every bubble the Profile colour, matching the
   * individual markers. A config change re-applies the bubble paint in place.
   */
  platformColor?: PlatformColorConfig;
}

/**
 * Non-visual React component that owns the cluster layer's
 * MapLibre lifecycle: layer registration, click handler binding,
 * a11y region updates.
 *
 * Renders no DOM (the visible surface is WebGL inside MapLibre's
 * canvas; the a11y region is owned by the parent so it stays
 * mounted across this component's remount cycles).
 *
 * Cross-references:
 *   - REQ-UI-010 / REQ-UI-011.
 *   - REQ-UI-059.
 *   - ADR-016 Open Item 5.
 *   - docs/engineering/detail/storyboards/flows/03-pan-zoom-cluster/README.md.
 */
export function ClusterLayer(props: ClusterLayerProps): null {
  const { map, snapshot, liveRegionRef, platformColor } = props;

  // Depend on the colour config's primitive fields (not the object identity) so
  // the attach effect re-runs — and re-applies the bubble paint — exactly when
  // the Profile colour mode/colour changes (REQ-UI-086).
  const platformColorMode = platformColor?.mode;
  const platformSingleColor = platformColor?.singleColor;
  // The per-status override map (``status_colors``) — serialized to a stable
  // primitive so it's a usable effect dep (inc-items 4/5; the cluster bubble
  // recolours with the markers). Read back via a ref inside the effect so the
  // deps stay honest (mirrors the snapshot layer).
  const platformStatusColors = platformColor?.statusColors;
  const platformStatusColorsKey = stableOverrideKey(platformStatusColors);
  const platformStatusColorsRef = useRef(platformStatusColors);
  platformStatusColorsRef.current = platformStatusColors;

  // Hold the debounce timer in a ref so the cleanup can cancel
  // pending writes across renders + on unmount.
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cluster-layer lifecycle — split into a teardown effect and an
  // attach effect, mirroring :class:`SnapshotLayer`'s source-
  // lifecycle split (the 2026-05-27 rendering-cascade fix).
  //
  // # Why split (the flicker fix)
  //
  // The earlier single-effect form keyed BOTH attach and its cleanup
  // on the derived ``hasSnapshot`` boolean. That avoided re-attaching
  // on same-key snapshot-object changes, but it still detached the
  // cluster layers whenever ``hasSnapshot`` flipped to ``false`` —
  // which happens on *every* pan/zoom-driven snapshot refetch:
  // :func:`useSnapshot` is keyed on the viewport bbox and has no
  // ``keepPreviousData``, so when the viewport settles the query
  // re-keys and ``data`` is briefly ``undefined`` → :class:`Map`
  // passes ``snapshot={null}``. The cleanup ran
  // :func:`detachClusterLayers`, blinking the cluster circles + count
  // glyphs out and back until the new snapshot resolved — the
  // reported flicker.
  //
  // With detach owned solely by the unmount effect, the layers stay
  // attached across refetches. Snapshot updates flow through
  // :class:`SnapshotLayer`'s :meth:`GeoJSONSource.setData` against the
  // live source; MapLibre re-clusters in place — no layer churn.
  const hasSnapshot = snapshot !== null;

  // Teardown effect — detach ONCE on unmount (or if ``map`` itself
  // changes, which is rare — only a full Map remount). Deliberately
  // NOT keyed on the snapshot, so a snapshot→null transition during a
  // pan/zoom refetch does not tear the layers down.
  useEffect(() => {
    return () => {
      detachClusterLayers(map);
    };
  }, [map]);

  // Attach effect — idempotent registration once the snapshot source
  // exists. Keyed on ``hasSnapshot`` so it attaches when the snapshot
  // first arrives (the signal that :class:`SnapshotLayer` has created
  // the source) and re-runs harmlessly on later toggles
  // (:func:`attachClusterLayers` no-ops when the layers are already
  // present). It performs NO teardown — detach is owned by the
  // unmount effect above.
  useEffect(() => {
    if (!hasSnapshot) {
      return;
    }
    // Defensive guard: in production :class:`SnapshotLayer`'s attach
    // effect runs first (sibling mount order) so the source is
    // present by the time we get here. Tests may swap that ordering;
    // keep the guard for safety.
    if (map.getSource(SNAPSHOT_SOURCE_ID) === undefined) {
      return;
    }
    // Reconstruct the colour config from its primitive deps below (keeps the
    // dependency array exhaustive without depending on the object identity).
    // attachClusterLayers re-applies the bubble colour on an existing layer, so
    // a Profile colour mode / single-colour / per-status override change repaints
    // in place (REQ-UI-086). ``status_colors`` is read via its ref (the reactive
    // trigger is ``platformStatusColorsKey``; the ref holds the same map the key
    // was computed from this render).
    attachClusterLayers(map, {
      mode: platformColorMode,
      singleColor: platformSingleColor,
      statusColors: platformStatusColorsRef.current,
    });
  }, [map, hasSnapshot, platformColorMode, platformSingleColor, platformStatusColorsKey]);

  // Click handler effect. The handler queries the rendered cluster
  // features under the click position and flyTos to the expansion
  // zoom. Bound after the layers mount; unbound on cleanup.
  useEffect(() => {
    const onClick = (event: MapMouseEvent): void => {
      const features = map.queryRenderedFeatures(event.point, {
        layers: [CLUSTER_LAYER_ID],
      });
      const feature = features[0];
      if (feature === undefined) {
        return;
      }
      handleClusterClick(map, feature);
    };
    map.on("click", CLUSTER_LAYER_ID, onClick);
    return () => {
      map.off("click", CLUSTER_LAYER_ID, onClick);
    };
  }, [map]);

  // A11y summary update effect. Listens to ``moveend`` + the source
  // ``data`` events; writes a one-sentence summary into the
  // ``aria-live`` region. Debounced per
  // :const:`CLUSTER_SUMMARY_DEBOUNCE_MS`.
  useEffect(() => {
    const liveEl = liveRegionRef?.current ?? null;
    if (liveEl === null) {
      return undefined;
    }
    const scheduleUpdate = (): void => {
      if (debounceTimerRef.current !== null) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        const counts = computeClusterCounts(map);
        liveEl.textContent = clusterSummaryMessage(counts);
        debounceTimerRef.current = null;
      }, CLUSTER_SUMMARY_DEBOUNCE_MS);
    };
    // Compute an immediate summary so the region isn't empty on
    // first mount (it would read as "no information" to a screen
    // reader otherwise — that's a worse failure mode than a slight
    // initial delay).
    const initial = computeClusterCounts(map);
    liveEl.textContent = clusterSummaryMessage(initial);

    map.on("moveend", scheduleUpdate);
    // Source ``data`` events fire on snapshot replacement AND on
    // SSE delta-merge — both are legitimate cluster-recompute
    // triggers, and the debounce keeps SSE-driven announcements
    // from spamming the screen reader.
    map.on("sourcedata", scheduleUpdate);

    return () => {
      map.off("moveend", scheduleUpdate);
      map.off("sourcedata", scheduleUpdate);
      if (debounceTimerRef.current !== null) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, [map, liveRegionRef]);

  return null;
}
